/**
 * Clipboard state store with WebSocket sync.
 */
import { create } from 'zustand';
import { toast } from 'react-hot-toast';
import { clipboardApi, ClipboardItem } from '../api/clipboard';
import { ClipboardWebSocket } from '../api/websocket';

interface ClipboardState {
  items: ClipboardItem[];
  total: number;
  isLoading: boolean;
  ws: ClipboardWebSocket | null;

  loadItems: () => Promise<void>;
  createItem: (data: any) => Promise<void>;
  deleteItem: (id: string) => Promise<void>;
  connectWebSocket: (token: string) => void;
  disconnectWebSocket: () => void;
}

export const useClipboardStore = create<ClipboardState>((set, get) => ({
  items: [],
  total: 0,
  isLoading: false,
  ws: null,

  loadItems: async () => {
    set({ isLoading: true });

    try {
      // Load items with maximum allowed page size of 100
      const response = await clipboardApi.list(1, 100);
      set({ items: response.items, total: response.total, isLoading: false });
    } catch (error: any) {
      console.error('Failed to load clipboard items:', error);
      toast.error('Failed to load clipboard items');
      set({ isLoading: false });
      throw error;
    }
  },

  createItem: async (data) => {
    try {
      const item = await clipboardApi.create(data);
      set((state) => ({
        items: [item, ...state.items],
        total: state.total + 1
      }));
      toast.success('Item added to clipboard');
    } catch (error: any) {
      console.error('Failed to create clipboard item:', error);
      toast.error('Failed to add item to clipboard');
      throw error;
    }
  },

  deleteItem: async (id: string) => {
    try {
      await clipboardApi.delete(id);
      set((state) => ({
        items: state.items.filter((item) => item.id !== id),
        total: Math.max(0, state.total - 1)
      }));
      toast.success('Item deleted');
    } catch (error: any) {
      console.error('Failed to delete clipboard item:', error);
      toast.error('Failed to delete item');
      throw error;
    }
  },

  connectWebSocket: (token: string) => {
    const ws = new ClipboardWebSocket(token);

    // Handle clipboard updates - listen for 'update' type messages
    ws.on('update', (message) => {
      const { action, item_id } = message;

      // Reload items when clipboard changes
      // TODO: Optimize with differential updates instead of full reload
      get().loadItems().catch((error) => {
        console.error('Failed to reload items after WebSocket update:', error);
      });
    });

    ws.connect();

    set({ ws });
  },

  disconnectWebSocket: () => {
    const { ws } = get();

    if (ws) {
      ws.disconnect();
      set({ ws: null });
    }
  },
}));
